import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import AppShell from '@/components/layout/AppShell';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { GoogleWorkspacePanel } from '@/components/integrations/GoogleWorkspacePanel';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import {
  User,
  Lock,
  Mail,
  Cloud,
  Server,
  Loader2,
  Upload,
  Check,
  ExternalLink,
  BookOpen,
  Presentation,
  Shield,
  Download,
  FileText
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUserRole } from '@/hooks/useUserRole';
import RoleRequestForm from '@/components/settings/RoleRequestForm';
import { useLocalization } from '@/hooks/useLocalization';

const Settings = () => {
  const { t } = useLocalization();
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const defaultTab = tabParam && ['profile', 'role', 'security', 'integrations', 'mcp', 'resources'].includes(tabParam) ? tabParam : 'profile';
  const { role, loading: roleLoading } = useUserRole();
  
  // Profile state
  const [displayName, setDisplayName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  
  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  
  // MCP Settings state
  const [mcpServerUrl, setMcpServerUrl] = useState('');
  const [mcpApiKey, setMcpApiKey] = useState('');

  const fetchProfile = useCallback(async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        setDisplayName(data.display_name || '');
        setAvatarUrl(data.avatar_url || '');
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error fetching profile:', errorMessage);
    } finally {
      setIsLoadingProfile(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchProfile();
    }
  }, [user, fetchProfile]);


  const handleUpdateProfile = async () => {
    if (!user) return;
    
    setIsSavingProfile(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .upsert({
          user_id: user.id,
          display_name: displayName,
          avatar_url: avatarUrl,
        });

      if (error) throw error;

      toast({
        title: t('Profile Updated'),
        description: t('Your profile has been updated successfully.'),
      });
    } catch (error: unknown) {
      const errMsg = error instanceof Error ? error.message : 'An error occurred';
      toast({
        title: t('Error'),
        description: errMsg,
        variant: 'destructive',
      });
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;

    setIsUploadingAvatar(true);
    try {
      const fileExt = file.name.split('.').pop();
      const filePath = `${user.id}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      setAvatarUrl(publicUrl);
      
      // Auto-save avatar URL
      await supabase
        .from('profiles')
        .upsert({
          user_id: user.id,
          avatar_url: publicUrl,
        });

      toast({
        title: t('Avatar Uploaded'),
        description: t('Your avatar has been updated.'),
      });
    } catch (error: unknown) {
      const errMsg = error instanceof Error ? error.message : 'Upload failed';
      toast({
        title: t('Upload Failed'),
        description: errMsg,
        variant: 'destructive',
      });
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast({
        title: t('Error'),
        description: t('New passwords do not match.'),
        variant: 'destructive',
      });
      return;
    }

    if (newPassword.length < 6) {
      toast({
        title: t('Error'),
        description: t('Password must be at least 6 characters.'),
        variant: 'destructive',
      });
      return;
    }

    setIsChangingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      toast({
        title: t('Password Changed'),
        description: t('Your password has been updated successfully.'),
      });
      
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: unknown) {
      const errMsg = error instanceof Error ? error.message : 'An error occurred';
      toast({
        title: t('Error'),
        description: errMsg,
        variant: 'destructive',
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const getInitials = (email: string) => {
    return email.substring(0, 2).toUpperCase();
  };

  return (
    <AppShell activeView="settings">
      <div className="max-w-4xl mx-auto px-4 py-2">
        <h1 className="text-2xl font-semibold mb-6">{t('Settings')}</h1>
        <Tabs defaultValue={defaultTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-6 lg:w-auto lg:inline-grid">
            <TabsTrigger value="profile" className="gap-2">
              <User className="h-4 w-4" />
              <span className="hidden sm:inline">{t('Profile')}</span>
            </TabsTrigger>
            <TabsTrigger value="role" className="gap-2">
              <Shield className="h-4 w-4" />
              <span className="hidden sm:inline">{t('Role')}</span>
            </TabsTrigger>
            <TabsTrigger value="security" className="gap-2">
              <Lock className="h-4 w-4" />
              <span className="hidden sm:inline">{t('Security')}</span>
            </TabsTrigger>
            <TabsTrigger value="integrations" className="gap-2">
              <Cloud className="h-4 w-4" />
              <span className="hidden sm:inline">{t('Integrations')}</span>
            </TabsTrigger>
            <TabsTrigger value="mcp" className="gap-2">
              <Server className="h-4 w-4" />
              <span className="hidden sm:inline">MCP</span>
            </TabsTrigger>
            <TabsTrigger value="resources" className="gap-2">
              <BookOpen className="h-4 w-4" />
              <span className="hidden sm:inline">{t('Resources')}</span>
            </TabsTrigger>
          </TabsList>

          {/* Role Tab */}
          <TabsContent value="role" className="space-y-6">
            {user && !roleLoading && (
              <RoleRequestForm userId={user.id} currentRole={role} />
            )}
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{t('Profile Information')}</CardTitle>
                <CardDescription>
                  
                  {t('Update your profile details and photo')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {isLoadingProfile ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-6">
                      <Avatar className="h-20 w-20">
                        <AvatarImage src={avatarUrl} />
                        <AvatarFallback className="text-lg">
                          {getInitials(user?.email || 'U')}
                        </AvatarFallback>
                      </Avatar>
                      <div className="space-y-2">
                        <Label htmlFor="avatar-upload" className="cursor-pointer">
                          <div className="flex items-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 transition-colors">
                            {isUploadingAvatar ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Upload className="h-4 w-4" />
                            )}
                            
                            {t('Upload Photo')}
                          </div>
                          <Input
                            id="avatar-upload"
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleAvatarUpload}
                            disabled={isUploadingAvatar}
                          />
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          
                          {t('JPG, PNG or GIF. Max 2MB.')}
                        </p>
                      </div>
                    </div>

                    <Separator />

                    <div className="grid gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="email">{t('Email')}</Label>
                        <Input
                          id="email"
                          type="email"
                          value={user?.email || ''}
                          disabled
                          className="bg-muted"
                        />
                        <p className="text-xs text-muted-foreground">
                          
                          {t('Email cannot be changed')}
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="display-name">{t('Display Name')}</Label>
                        <Input
                          id="display-name"
                          placeholder={t('Enter your display name')}
                          value={displayName}
                          onChange={(e) => setDisplayName(e.target.value)}
                        />
                      </div>
                    </div>

                    <Button onClick={handleUpdateProfile} disabled={isSavingProfile}>
                      {isSavingProfile ? (
                        <Loader2 className="h-4 w-4 animate-spin me-2" />
                      ) : (
                        <Check className="h-4 w-4 me-2" />
                      )}
                      
                      {t('Save Changes')}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{t('Change Password')}</CardTitle>
                <CardDescription>
                  
                  {t('Update your password to keep your account secure')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">{t('New Password')}</Label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">{t('Confirm New Password')}</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </div>
                <Button onClick={handleChangePassword} disabled={isChangingPassword}>
                  {isChangingPassword ? (
                    <Loader2 className="h-4 w-4 animate-spin me-2" />
                  ) : (
                    <Lock className="h-4 w-4 me-2" />
                  )}
                  
                  {t('Update Password')}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Integrations Tab */}
          <TabsContent value="integrations" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{t('Email Accounts')}</CardTitle>
                <CardDescription>
                  
                  {t('Connect email accounts to sync with Masira')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <GoogleWorkspacePanel />
                
                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                      <Mail className="h-5 w-5 text-blue-500" />
                    </div>
                    <div>
                      <p className="font-medium">{t('Outlook')}</p>
                      <p className="text-sm text-muted-foreground">{t('Connect your Outlook account')}</p>
                    </div>
                  </div>
                  <Button variant="outline" className="gap-2">
                    <ExternalLink className="h-4 w-4" />
                    
                    {t('Connect')}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('Cloud Storage')}</CardTitle>
                <CardDescription>
                  
                  {t('Connect cloud storage services to access your files')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                      <Cloud className="h-5 w-5 text-yellow-500" />
                    </div>
                    <div>
                      <p className="font-medium">{t('Google Drive')}</p>
                      <p className="text-sm text-muted-foreground">{t('Access files from Google Drive')}</p>
                    </div>
                  </div>
                  <Button variant="outline" className="gap-2">
                    <ExternalLink className="h-4 w-4" />
                    
                    {t('Connect')}
                  </Button>
                </div>
                
                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-sky-500/10 flex items-center justify-center">
                      <Cloud className="h-5 w-5 text-sky-500" />
                    </div>
                    <div>
                      <p className="font-medium">{t('OneDrive')}</p>
                      <p className="text-sm text-muted-foreground">{t('Access files from Microsoft OneDrive')}</p>
                    </div>
                  </div>
                  <Button variant="outline" className="gap-2">
                    <ExternalLink className="h-4 w-4" />
                    
                    {t('Connect')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* MCP Tab */}
          <TabsContent value="mcp" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{t('MCP Server Configuration')}</CardTitle>
                <CardDescription>
                  
                  {t('Configure your Model Context Protocol server settings')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="mcp-url">{t('MCP Server URL')}</Label>
                  <Input
                    id="mcp-url"
                    placeholder="https://your-mcp-server.com"
                    value={mcpServerUrl}
                    onChange={(e) => setMcpServerUrl(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    
                    {t('The URL of your MCP server endpoint')}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="mcp-key">{t('API Key')}</Label>
                  <Input
                    id="mcp-key"
                    type="password"
                    placeholder="••••••••••••••••"
                    value={mcpApiKey}
                    onChange={(e) => setMcpApiKey(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    
                    {t('Your MCP server authentication key')}
                  </p>
                </div>

                <Separator />

                <div className="p-4 bg-muted/50 rounded-lg">
                  <h4 className="font-medium mb-2">{t('Available MCP Tools')}</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    
                    {t('Connect to your MCP server to see available tools and capabilities.')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-primary/10 text-primary rounded text-xs">
                      
                      {t('n8n Workflows')}
                    </span>
                    <span className="px-2 py-1 bg-primary/10 text-primary rounded text-xs">
                      
                      {t('Notion Pages')}
                    </span>
                    <span className="px-2 py-1 bg-primary/10 text-primary rounded text-xs">
                      
                      {t('Linear Issues')}
                    </span>
                  </div>
                </div>

                <Button className="gap-2">
                  <Server className="h-4 w-4" />
                  
                  {t('Save MCP Settings')}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Resources Tab */}
          <TabsContent value="resources" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{t('Product Resources')}</CardTitle>
                <CardDescription>
                  
                  {t('Access documentation and presentation materials')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Presentation className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{t('Pitch Deck')}</p>
                      <p className="text-sm text-muted-foreground">{t('View and export the product pitch deck')}</p>
                    </div>
                  </div>
                  <Button variant="outline" className="gap-2" onClick={() => navigate('/pitch-deck')}>
                    <ExternalLink className="h-4 w-4" />
                    
                    {t('Open')}
                  </Button>
                </div>
                
                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                      <BookOpen className="h-5 w-5 text-emerald-500" />
                    </div>
                    <div>
                      <p className="font-medium">{t('Product Documentation')}</p>
                      <p className="text-sm text-muted-foreground">{t('Complete product details and specifications')}</p>
                    </div>
                  </div>
                  <Button variant="outline" className="gap-2" onClick={() => navigate('/docs')}>
                    <ExternalLink className="h-4 w-4" />
                    
                    {t('Open')}
                  </Button>
                </div>

                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-violet-500/10 flex items-center justify-center">
                      <FileText className="h-5 w-5 text-violet-500" />
                    </div>
                    <div>
                      <p className="font-medium">{t('Project Blueprint')}</p>
                      <p className="text-sm text-muted-foreground">{t('Complete technical blueprint for replication')}</p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    className="gap-2" 
                    onClick={async () => {
                      try {
                        const response = await fetch('/docs/Masira-Project-Blueprint.md');
                        const content = await response.text();
                        const blob = new Blob([content], { type: 'text/markdown' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'Masira-Project-Blueprint.md';
                        a.click();
                        URL.revokeObjectURL(url);
                        toast({ title: t('Download Started'), description: t('Blueprint file is downloading.') });
                      } catch (error) {
                        toast({ title: t('Download Failed'), description: t('Could not download the blueprint file.'), variant: 'destructive' });
                      }
                    }}
                  >
                    <Download className="h-4 w-4" />
                    
                    {t('Download')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
};

export default Settings;
